import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import i18n from "i18next";
import PropTypes from "prop-types";
import { getTrueClasses } from "../../../helpers";
import { MediaAuthContext } from "../../../store/context/media-auth-context";
import { SLIDES } from "./index.content";
import EN from "../../../locales/en/video.json";
import ES from "../../../locales/es/video.json";
import FR from "../../../locales/fr/video.json";
import WrapSlider from "../WrapSlider";
import ArrowRight from "./ArrowRight";
import ArrowLeft from "./ArrowLeft";
import Item from "./Item";
import "./index.scss";

const resources = {
  en: { translation: EN },
  es: { translation: ES },
  fr: { translation: FR },
};

const SETTINGS_MOB = {
  centerMode: true,
  initialSlide: 0,
  touchThreshold: 40,
};
const SLIDES_BREAKPOINTS = {
  MOBILE: 1,
  TABLET: 3,
  DESKTOP: 4,
};

const Arrow = ({ className, style, onClick, arrowDirection }) => (
  <div className={className} style={{ ...style }} onClick={onClick}>
    {arrowDirection === "right" ? <ArrowLeft /> : null}
    {arrowDirection === "left" ? <ArrowRight /> : null}
  </div>
);

const Video = (props) => {
  const {
    setUrlYoutubeModal,
    customClass,
    slides = SLIDES,
    language = "en",
  } = props;
  const {
    media: { isNotInitMobile, isTablet },
  } = useContext(MediaAuthContext);
  const [coordStartClick, setCoordStartClick] = useState({ x: 0, y: 0 });
  const [isRendered, setIsRendered] = useState(false);
  const LANGUAGE = useMemo(
    () => (resources[language] ? language : "en"),
    [language]
  );
  i18n.init({
    lng: LANGUAGE,
    resources,
  });
  const t = i18n.t;
  const numberOfSlides = useMemo(
    () =>
      isNotInitMobile
        ? SLIDES_BREAKPOINTS.MOBILE
        : isTablet
          ? SLIDES_BREAKPOINTS.TABLET
          : SLIDES_BREAKPOINTS.DESKTOP,
    [isNotInitMobile, isTablet]
  );
  const settings = {
    className: "GC-V-slider",
    dots: true,
    speed: 500,
    infinite: false,
    slidesToShow: numberOfSlides,
    slidesToScroll: numberOfSlides,
    variableWidth: true,
    nextArrow: <Arrow arrowDirection="left" />,
    prevArrow: <Arrow arrowDirection="right" />,
    ...(isNotInitMobile && SETTINGS_MOB),
  };

  const handleMouseDown = useCallback((e) => {
    setCoordStartClick({
      x: e.clientX,
      y: e.clientY,
    });
  }, []);

  const handleClickVideo = useCallback(
    (e, url) => {
      const isSwipe =
        Math.abs(coordStartClick.x - e.clientX) > 3 ||
        Math.abs(coordStartClick.y - e.clientY) > 3;
      if (!isSwipe) {
        setUrlYoutubeModal(url);
      }
    },
    [coordStartClick, setUrlYoutubeModal]
  );
  const items = useMemo(() => {
    return slides.map(({ name, url, position, text, screen }, i) => (
      <Item
        key={name}
        i={i}
        handleMouseDown={handleMouseDown}
        handleClickVideo={handleClickVideo}
        position={t(position)}
        name={t(name)}
        url={url}
        text={t(text)}
        JPG_2X={screen.JPG_2X}
      />
    ));
  }, [slides, handleMouseDown, handleClickVideo]);

  useEffect(() => {
    setIsRendered(true);
  }, []);

  return (
    <section className={getTrueClasses("GC-V", customClass)}>
      <div className="GC-V-content">
        {isRendered ? (
          <WrapSlider settings={settings}>{items}</WrapSlider>
        ) : null}
      </div>
    </section>
  );
};

Video.propTypes = {
  setUrlYoutubeModal: PropTypes.func,
  customClass: PropTypes.string,
  slides: PropTypes.array,
  language: PropTypes.string,
};

export default Video;
